import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useState } from "react";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { isEqual } from 'lodash';
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import CustomLoader from "../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import {
    AncillarySubtype,
    getCarGroupsByLocation,
    getCompany,
    getCoupon,
    getMobilePrefixes,
    getProduct,
    getRatePlan,
    IRangeDate,
    RESPONSE_CHECK_CODE,
    RESPONSE_TYPE_SERVICE,
    getProductLeisure,
    getCheckAffiliated
} from "../../../api/services";
import { CarGroupType, ReactQueryKey, RentalType } from "../../../types";
import {getCarGroupCode, LOCALE_DATE_TIME_FORMAT} from "../../../utils";
import { bookingIdState, businessAgreementCheckValidate, businessAgreementCig, expirationDateTimeState, idSmProductDefaultByDraftState, isBusinessContinuityMode, isLeisureCode, mandatoryDossierIsRequired, rangeDateValidity, ratePlanOfferIDState, requestUpdateTimeState, stepState } from "../recoil";
import { IFormValues, InsertBookingStateAction, InsertBookingStep, InsertBookinLocationState } from "../types";

const DefaultStateManager = () => {

    const { t } = useTranslation('booking');

    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const location = useLocation();
    const locationState = location.state as InsertBookinLocationState | null;

    const setStep = useSetRecoilState(stepState);
    const setBookingId = useSetRecoilState(bookingIdState);
    const setExpirationDateTime = useSetRecoilState(expirationDateTimeState);
    const setRequestUpdateTime = useSetRecoilState(requestUpdateTimeState);
    const idSmProductDefault = useSetRecoilState(idSmProductDefaultByDraftState);
    const [flagLeisureCode, setLeisureCode] = useRecoilState(isLeisureCode);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);

    const setBusinessAgreementCheckValidate = useSetRecoilState<boolean | undefined>(businessAgreementCheckValidate);
    const setBusinessAgreementHasCig = useSetRecoilState<boolean | undefined>(businessAgreementCig);
    const seMandatoryDossierIsRequired = useSetRecoilState<boolean | undefined>(mandatoryDossierIsRequired);
    const setRangeDate = useSetRecoilState<IRangeDate | undefined>(rangeDateValidity);

    const formContext = useFormContext<IFormValues>();

    const queryClient = useQueryClient();

    const [loading, setLoading] = useState<boolean>(false);

    const [ratePlanOfferID, setRatePlanOfferID] = useRecoilState(ratePlanOfferIDState);

    // const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    // const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const tenantWatch = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealerWatch = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const locationBookingTenant = locationState?.booking.tenant;
    const locationBookingDealer = locationState?.booking.dealer;

    const [tenant, dealer] = [
        InsertBookingStateAction.Copy,
        InsertBookingStateAction.CopyWithRatePlan,
    ].includes(locationState?.action as any)
        ? [locationBookingTenant, locationBookingDealer]
        : [tenantWatch, dealerWatch];

    const setDefaultValues = useCallback(async (tenant: string | null, dealer: string | null) => {
        setLoading(true);
        try {
            const defaultRentalType = !locationState ?
                formContext.getValues('basicDetails.rentalType') :
                locationState.booking.rentalType;

            const productParams = {
                businessContinuityInsToolMode: businessContinuityMode,
                segment: defaultRentalType,
                tenant: tenant as string,
                dealer: dealer as string
            }
            let product;
            if(defaultRentalType === RentalType.RETAIL) {

                product = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.Product, {segment: defaultRentalType, tenant: tenant as string, dealer: dealer as string}],
                    queryFn: () => getProduct(productParams)
                });

                let coupon;
                if(!!locationState?.booking?.discountCode && locationState.action === InsertBookingStateAction.DraftEdit) {

                    formContext.setValue('extraServices.discountCode', locationState?.booking?.discountCode ?? undefined);
                    formContext.setValue('extraServices.fcaBankCardNumber', locationState?.booking.fcaBankCardNumber ?? undefined);
                    formContext.setValue('extraServices.discountType', !!locationState?.booking.discountCode ? 'promo' : !!locationState?.booking.fcaBankCardNumber ? 'fca' : null);

                    const couponParams = {
                        pickUpDate: locationState?.booking?.pickupDate,
                        dropoffDate: locationState?.booking?.dropoffDate,
                        pickUpLocation: locationState?.booking?.pickupLocation.id,
                        dropOffLocation: locationState?.booking?.dropoffLocation.id,
                        promoCode: locationState?.booking?.discountCode,
                        carGroupCode: !!locationState?.booking?.carGroup ? (locationState?.booking?.carGroup.type === CarGroupType.Acriss ? locationState?.booking?.carGroup.acrissCode : !!locationState?.booking?.carGroup.sippCode ? locationState?.booking?.carGroup.sippCode : undefined) : undefined,
                        productCodes:  !!locationState?.booking?.idSmProduct ? [locationState?.booking?.idSmProduct] : [],
                        tenant: tenant as string,
                        dealer: dealer as string
                    }
                    coupon = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.Coupon, {tenant: tenant as string, dealer: dealer as string}],
                        queryFn: () => getCoupon(couponParams)
                    });

                    if (coupon?.code !== RESPONSE_CHECK_CODE.SUCCESS) {
                        formContext.setError('extraServices.discountCode', {
                            message: 'message.validation.invalid_code'
                        });
                    } else {
                        if (coupon.codeServiceType === RESPONSE_TYPE_SERVICE.leisure) {
                            setLeisureCode(true);
                        }
                    }
                }

                if(!!coupon && coupon.codeServiceType === RESPONSE_TYPE_SERVICE.leisure) {

                    const productLeisureParams = {
                        leisureCode: locationState?.booking?.discountCode ?? '',
                        productList: coupon.leisureResult?.productList ?? [],
                        pickUpDate: locationState?.booking?.pickupDate ?? '',
                        tenant: tenant as string,
                        dealer: dealer as string
                    };
                    product = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.Product, productParams],
                        queryFn: () => getProductLeisure(productLeisureParams)
                    });
                }
            } else {
                const customerCode = locationState?.booking?.customerCode as string;
                product = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.Product, productParams],
                    queryFn: () => getCompany(customerCode, locationState?.booking?.pickupDate || '', tenant as string, dealer as string)
                });
                // set state for corporate form
                if(!!customerCode) {
                    setBusinessAgreementCheckValidate(!!product);
                    setBusinessAgreementHasCig(!!product?.cig && product?.cig?.length > 0);
                    seMandatoryDossierIsRequired(product?.mandatoryDossier);
                    setRangeDate(!!product?.rangeDate ? product?.rangeDate : {startDate: null, endDate: null});
                } else {
                    setBusinessAgreementCheckValidate(false);
                    setBusinessAgreementHasCig(false);
                    seMandatoryDossierIsRequired(false);
                    setRangeDate({startDate: null, endDate: null});
                }
            }

            if (locationState && !!product) {
                const booking = locationState.booking;
                const carGroupsParams = {
                    pickupLocation: booking.pickupLocation.id,
                    dropoffLocation: booking.dropoffLocation.id,
                    requestDate: businessContinuityMode ? formContext.getValues("basicDetails.bookingDateTime")?.toISOString() || new Date().toISOString() : new Date().toISOString(),
                    pickupDate: booking.pickupDate,
                    dropoffDate: booking.dropoffDate,
                    products: product.products,
                    tenant: tenant as string,
                    dealer: dealer as string
                };
                const carGroups = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.CarGroupsByLocation, carGroupsParams],
                    queryFn: () => getCarGroupsByLocation(carGroupsParams)
                });

                formContext.setValue(
                    'basicDetails.tenants',
                    booking.tenant
                );

                formContext.setValue(
                    'basicDetails.dealers',
                    booking.dealer
                );

                formContext.setValue(
                    'basicDetails.customerCode',
                    booking.customerCode
                );

                formContext.setValue(
                    'basicDetails.cig',
                    booking.cig
                );

                formContext.setValue(
                    'basicDetails.dossierNumber',
                    booking.dossierNumber
                );

                const defaultPickupLocation = product.locations
                    .find(v => v.id === booking.pickupLocation.id) || null;
                const defaultDropoffLocation = product.locations
                    .find(v => v.id === booking.dropoffLocation.id) || null;
                const defaultCarGroup = carGroups.carGroups
                    .find(v => v.acrissCode === booking.carGroup.acrissCode && v.sippCode === booking.carGroup.sippCode) || null;
                formContext.setValue('basicDetails.rentalType', defaultRentalType);
                formContext.setValue(
                    'basicDetails.pickupLocation',
                    defaultPickupLocation
                );
                formContext.setValue(
                    'basicDetails.dropoffLocation',
                    defaultDropoffLocation
                );
                formContext.setValue('basicDetails.pickupDate', dayjs(booking.pickupDate));
                formContext.setValue('basicDetails.dropoffDate', dayjs(booking.dropoffDate));
                formContext.setValue('basicDetails.carGroup', defaultCarGroup);
                if (
                    !defaultPickupLocation ||
                    !defaultDropoffLocation ||
                    !defaultCarGroup
                ) {
                    return;
                }

               if(!!booking.affiliationCode) {
                    const checkcodeAffiliated = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.AffiliationCode, {tenant: tenant as string, dealer: dealer as string}],
                        queryFn: () => getCheckAffiliated({tenant: tenant as string, dealer: dealer as string, genVendorCode: booking.affiliationCode}),
                    });
                }

                const idProfile = globalData?.user?.profiles
                    .map((p: any) => (
                        p.id
                    ))

                const ratePlanParams = {
                    pickupDate: dayjs(booking.pickupDate).format(LOCALE_DATE_TIME_FORMAT),
                    dropoffDate: dayjs(booking.dropoffDate).format(LOCALE_DATE_TIME_FORMAT),
                    pickupLocation: defaultPickupLocation.id,
                    dropoffLocation: defaultDropoffLocation.id,
                    carGroupCode: getCarGroupCode(defaultCarGroup),
                    products: product.products,
                    businessAgreement: booking.customerCode ?? null,
                    profileIdList: idProfile,
                    firstEdit: !!locationState.action && locationState.action === InsertBookingStateAction.CopyWithRatePlan,
                    offerId: !!locationState.action && locationState.action === InsertBookingStateAction.CopyWithRatePlan && !!booking.offerId ? booking.offerId : undefined,
                    requestDate: businessContinuityMode ? formContext.getValues("basicDetails.bookingDateTime")?.toISOString() || undefined : undefined,
                    tenant: tenant as string,
                    dealer: dealer as string
                }
                const ratePlan = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}],
                    queryFn: () => getRatePlan(ratePlanParams)
                });

                if(!!ratePlan.offerId && ratePlanOfferID != ratePlan.offerId)
                    setRatePlanOfferID(ratePlan.offerId);

                formContext.setValue(
                    'basicDetails.mileageIncluded',
                    ratePlan.mileageList.find(m => m.value === booking.mileageIncluded) ? booking.mileageIncluded : null
                );
                formContext.setValue(
                    'extraServices.affiliationCode',
                    booking.affiliationCode
                );
                formContext.setValue(
                    'extraServices.flightNumber',
                    booking.flightNumber
                );

                if(!!locationState.action) {
                    // set ancillaries
                    let ancillary: any = {};
                    for(let count = 0; count < booking.ancillaries.length; count ++) {
                        if (booking.ancillaries[count].subtype === AncillarySubtype.AdditionalDrivers) {
                            ancillary[AncillarySubtype.AdditionalDrivers] = booking.ancillaries[count].units;
                        }else {
                            const defaultObj =  ratePlan.ancillaries.filter((elem: any) => elem.ancillaryCode === booking.ancillaries[count].ancillaryCode)[0];

                            if(!!defaultObj) {
                                if(defaultObj.maxUnits > 1 ) {
                                    ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].units;
                                } else if (defaultObj.mileageNeeded) {
                                    ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].mileage;
                                } else {
                                    ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].selected;
                                }
                            }
                        }
                    }

                    // set extraServices form value
                    formContext.setValue('extraServices', {
                        ancillary: ancillary,
                        discountCode: booking.discountCode ?? undefined,
                        fcaBankCardNumber: booking?.fcaBankCardNumber ?? undefined,
                        discountOverprice: booking.discountOverprice ?? null,
                        note: '',
                        affiliationCode: booking.affiliationCode ?? '',
                        flightNumber: booking.flightNumber ?? '',
                        discountType: !!booking.discountCode ? 'promo' : (booking.fcaBankCardNumber ? 'fca' : null),
                        collectionState: booking.collectionState ?? '',
                        collectionStreetName: booking.collectionStreetName ?? undefined,
                        collectionStreetNumber: booking.collectionStreetNumber ?? undefined,
                        collectionPostalCode: booking.collectionPostalCode ?? undefined,
                        collectionCity: booking.collectionCity ?? undefined,
                        deliveryState: booking.deliveryState ?? '',
                        deliveryStreetName: booking.deliveryStreetName ?? undefined,
                        deliveryStreetNumber: booking.deliveryStreetNumber ?? undefined,
                        deliveryPostalCode: booking.deliveryPostalCode ?? undefined,
                        deliveryCity: booking.deliveryCity ?? undefined,
                        checkAffiliated: null,
                        checkCoupon: null
                    });

                    // set additional drivers form
                    const additionalDriversAncillary = ancillary[AncillarySubtype.AdditionalDrivers] as number | undefined;
                    if (additionalDriversAncillary ) {
                        let additionalDrivers = [];
                        if (booking?.additionalDrivers?.length !== additionalDriversAncillary) {
                            for (let i=0; i<additionalDriversAncillary; i++) {
                                additionalDrivers.push({
                                    id: '',
                                    name: '',
                                    surname: '',
                                    email: ''
                                })
                            }
                        } else {
                            additionalDrivers = booking?.additionalDrivers.map( (elem: any) => {
                                    elem. id = elem.customerId;
                                return elem;
                            });
                        }
                        formContext.setValue('additionalDrivers', additionalDrivers);
                    }
                }
                switch (locationState.action) {
                    case InsertBookingStateAction.Copy:
                    case InsertBookingStateAction.CopyWithRatePlan:
                        formContext.setValue('firstDriver', {
                            id: booking.firstDriver?.customerId ?? '',
                            name: booking.firstDriver?.name ?? '',
                            surname: booking.firstDriver?.surname ?? '',
                            birthDate: booking.firstDriver?.birthDate ? dayjs(booking.firstDriver.birthDate) : null,
                            birthNation: booking.firstDriver?.birthNation ?? '',
                            birthPlace: booking.firstDriver?.birthPlace ?? '',
                            residenceCountry: booking.firstDriver?.residenceCountry ?? '',
                            email: booking.firstDriver?.email ?? '',
                            mobile: booking.firstDriver?.mobile ?? '',
                            fiscalCode: booking.firstDriver?.fiscalCode ?? '',
                            drivingLicenseNumber: booking.firstDriver?.drivingLicenseNumber ?? '',
                            uuid: '',
                            mobilePrefix: null,
                            gender: booking.firstDriver?.gender ?? ''
                        });
                        formContext.setValue('invoice', {
                            id: booking.invoicePerson?.customerId ?? '',
                            type: booking.invoicePerson?.type ? booking.invoicePerson.type : booking.invoiceCompany?.type ? booking.invoiceCompany.type : '',
                            name: booking.invoicePerson?.name ?? '',
                            surname: booking.invoicePerson?.surname ?? '',
                            birthDate: booking.invoicePerson?.birthDate ? dayjs(booking.invoicePerson.birthDate) : null,
                            birthNation: booking.invoicePerson?.birthNation ?? '',
                            birthPlace: booking.invoicePerson?.birthPlace ?? '',
                            residenceCountry: booking.invoicePerson?.residenceCountry ?? '',
                            fiscalCode: booking.invoicePerson?.fiscalCode ? booking.invoicePerson.fiscalCode : booking.invoiceCompany?.fiscalCode ? booking.invoiceCompany.fiscalCode : '',
                            drivingLicenseNumber: booking.invoicePerson?.drivingLicenseNumber ?? '',
                            email: booking.invoicePerson?.email ?? '',
                            mobile: booking.invoicePerson?.mobile ?? '',
                            billingCountry: booking.invoicePerson?.billingCountry ? booking.invoicePerson.billingCountry : booking.invoiceCompany?.billingCountry ? booking.invoiceCompany.billingCountry : null,
                            billingCity: booking.invoicePerson?.billingCity ? booking.invoicePerson.billingCity : booking.invoiceCompany?.billingCity ? booking.invoiceCompany.billingCity : '',
                            billingStreetName: booking.invoicePerson?.billingStreetName ? booking.invoicePerson.billingStreetName : booking.invoiceCompany?.billingStreetName ? booking.invoiceCompany.billingStreetName : '',
                            billingStreetNumber: booking.invoicePerson?.billingStreetNumber ? booking.invoicePerson.billingStreetNumber : booking.invoiceCompany?.billingStreetNumber ? booking.invoiceCompany.billingStreetNumber : '',
                            billingPostalCode: booking.invoicePerson?.billingPostalCode ? booking.invoicePerson.billingPostalCode : booking.invoiceCompany?.billingPostalCode ? booking.invoiceCompany.billingPostalCode : '',
                            certifiedEmail: booking.invoicePerson?.pec ? booking.invoicePerson.pec : booking.invoiceCompany?.pec ? booking.invoiceCompany.pec : '',
                            vatNumber: booking.invoiceCompany?.vatNumber ? booking.invoiceCompany.vatNumber : '',
                            vatNumberCountry: booking.invoiceCompany?.vatNumberCountry ? booking.invoiceCompany.vatNumberCountry : '',
                            siren: booking.invoiceCompany?.siren ? booking.invoiceCompany.siren : '',
                            siret: booking.invoiceCompany?.siret ? booking.invoiceCompany.siret : '',
                            invoiceCode: booking.invoiceCompany?.invoiceCode ? booking.invoiceCompany.invoiceCode : '',
                            companyName: booking.invoiceCompany?.companyName ?? '',
                            uuid: '',
                            mobilePrefix: null,
                            billingProvince: booking.invoicePerson?.billingProvince ? booking.invoicePerson.billingProvince : booking.invoiceCompany?.billingProvince ? booking.invoiceCompany.billingProvince : null,
                            linkedToBusinessAgreement: false,
                            legalEntity: booking.invoiceCompany?.legalEntity ? booking.invoiceCompany?.legalEntity : null,
                            publicAuthority: booking.invoiceCompany?.publicAuthority === '1',
                            paIdNumber: booking.invoiceCompany?.paIdNumber ?? '',

                        });
                        formContext.setValue('extraServices.discountCode', undefined);
                        formContext.setValue('extraServices.fcaBankCardNumber', undefined);
                        formContext.setValue('extraServices.discountType',  null);

                        const mobilePrefixes = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.MobilePrefixes],
                            queryFn: () => getMobilePrefixes()
                        });

                        // set mobile prefix by booking for first driver
                        if (!!booking?.firstDriver?.mobilePrefix) {
                            const foundMobilePrefixFirstDriver = mobilePrefixes?.find(mp => isEqual(mp, booking?.firstDriver?.mobilePrefix));
                                formContext.setValue('firstDriver.mobilePrefix', foundMobilePrefixFirstDriver ?? null);
                        }

                        // set mobile prefix by booking for billingOwner
                        if (!!booking?.invoicePerson?.mobilePrefix) {
                            const foundMobilePrefixBillingOwner = mobilePrefixes?.find(mp => isEqual(mp, booking?.invoicePerson?.mobilePrefix));
                            formContext.setValue('invoice.mobilePrefix', foundMobilePrefixBillingOwner ?? null);
                        }

                        setStep(InsertBookingStep.AVAILABILITY_PRICE);
                        break;
                    case InsertBookingStateAction.DraftEdit:
                        setBookingId(booking.id);
                        idSmProductDefault(booking.idSmProduct);
                        setExpirationDateTime(dayjs(booking.expirationDateTime));
                        setRequestUpdateTime(booking.requestUpdateDate);
                        setStep(InsertBookingStep.CUSTOMER_DETAILS);
                        break;
                }
            }
            else if (globalData.user.mobilityStores.length === 1) {
                const defaultLocation = globalData.user.mobilityStores[0];
                if (product && product.locations) {
                    formContext.setValue(
                        'basicDetails.pickupLocation',
                        product.locations.find(v => v.id === defaultLocation.sapId) || null
                    );
                    formContext.setValue(
                        'basicDetails.dropoffLocation',
                        product.locations.find(v => v.id === defaultLocation.sapId) || null
                    );
                } else {
                    formContext.setValue('basicDetails.pickupLocation', null);
                    formContext.setValue('basicDetails.dropoffLocation', null);
                }
            }
        }
        catch (error){
            console.log('e', error);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            setStep(InsertBookingStep.AVAILABILITY_PRICE);
        }
        finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!!tenant && !!dealer) {
            setDefaultValues(tenant, dealer);
        }
    }, [businessContinuityMode, tenant, dealer]);

    return (
        <CustomLoader loading={loading} />
    );

};

export default DefaultStateManager;
